<template>
  <FontAwesomeLayers v-tooltip="renderedTooltip">
    <FaIcon v-bind="{...icon.icon,...attrs}" />
    <FaIcon v-bind="icon.mask" v-if="!!icon?.mask" />
    <FaIcon
      v-if="ranOnGateway"
      :icon="GATEWAY_DEFAULT_ICON"
      transform="shrink-6 right-10 up-6"
      color="var(--color-warning-400)"
    />
  </FontAwesomeLayers>
</template>

<script setup lang="ts">
import { UNKNOWN_RULE_ACTION, ruleActionOverrides } from '@/models/rule/utils/rule-actions';
import { FontAwesomeLayers } from '@fortawesome/vue-fontawesome';
import { qx_eFlowTraceActionStatus_enum } from '@/graphql/types';
import { GATEWAY_DEFAULT_ICON } from '@/constants/gateway';
import { ICON } from '@/constants/icons';
import { FlowActions } from '@/models/rule/types';
import { FAIcon, FAIconName } from "@/types";
import { computed, useAttrs } from 'vue';

const props = defineProps<{
  type?: FlowActions,
  status?: qx_eFlowTraceActionStatus_enum,
  tooltip?: string,
  ranOnGateway?: boolean,
}>();

const attrs = useAttrs();

const ACTION_STATUS_LAYERS = new Map<qx_eFlowTraceActionStatus_enum, {
  base: FAIconName,
  badge: FAIconName,
  color: string,
  baseTransform: string,
  badgeTransform: string,
}>([
  [qx_eFlowTraceActionStatus_enum.RateLimited, {
    base: ICON.TRIANGLE,
    badge: ICON.ERROR,
    color: 'var(--color-info-500)',
    baseTransform: 'shrink-1 right-7 up-4',
    badgeTransform: 'shrink-5 right-8 up-4',
  }],
  [qx_eFlowTraceActionStatus_enum.RunOnCloudOnly, {
    base: ICON.CLOUD,
    badge: ICON.CLOUD,
    color: 'var(--color-info-500)',
    baseTransform: 'shrink-4 right-5 up-3',
    badgeTransform: 'shrink-5 right-6 up-4',
  }],
  [qx_eFlowTraceActionStatus_enum.Suppressed, {
    base: ICON.SQUARE,
    badge: ICON.WATER,
    color: 'var(--color-info-500)',
    baseTransform: 'shrink-4 right-5 up-3',
    badgeTransform: 'shrink-6 right-8 up-4',
  }],
  [qx_eFlowTraceActionStatus_enum.Delayed, {
    base: ICON.CIRCLE,
    badge: ICON.CLOCK,
    color: 'var(--color-warning-400)',
    baseTransform: 'shrink-4 right-5 up-3',
    badgeTransform: 'shrink-6 right-8 up-4',
  }],
]);

function getRuleTraceActionIcon(
  status?: qx_eFlowTraceActionStatus_enum,
  icon?: FAIconName | FAIcon
) {
  const layers = status && ACTION_STATUS_LAYERS.get(status);
  if (!layers) {
    return null;
  }
  return {
    icon: {
      icon: layers.base,
      mask: icon,
      transform: layers.baseTransform,
    },
    mask: {
      icon: layers.badge,
      transform: layers.badgeTransform,
      color: layers.color,
    },
  };
}

const hasActionConfig = computed(() => ruleActionOverrides?.[props?.type] || UNKNOWN_RULE_ACTION);
const defaultIcon = computed(() => hasActionConfig.value?.icon || ['fas', 'circle-question']);
const hasLayers = computed(() => getRuleTraceActionIcon(props.status, defaultIcon.value));

const icon = computed(() => {
  return hasLayers.value || {
    icon: {
      icon: defaultIcon.value,
    },
    mask: null
  }
});

const renderedTooltip = computed(() => {
  if (!hasActionConfig.value) {
    return props.tooltip ?? false;
  }
  return {
    html: true,
    content: [
      props.ranOnGateway ? '<small>Ran on Gateway</small>' : '',
      `<strong>${hasActionConfig.value.title}</strong>`,
      `<small>${props.tooltip || props.status || hasActionConfig.value.desc}</small>`,
    ].filter(Boolean).join('<br />')
  }
});
</script>
